import { useEffect, useId, useRef, useState } from "react";

import { AI_IMPORT_PROMPT, copyToClipboard } from "../lib/aiPrompt";

/** How long the "Copied!" confirmation stays up before reverting. */
const COPIED_RESET_MS = 2000;

/** idle → copied (auto-reverts) or failed (sticks, so the fallback stays open). */
type CopyState = "idle" | "copied" | "failed";

/**
 * The ⓘ affordance beside "Import from folder": a dismissible popover that
 * explains the folder shape the importer expects.
 *
 * It exists because the folder shape is easy to get subtly wrong. `parseFolder`
 * treats every immediate subfolder of the picked folder as one post, so picking
 * the parent of a set of batch folders quietly collapses each whole batch into
 * a single post instead of erroring.
 */
export function ImportHelp() {
  const [open, setOpen] = useState(false);
  const [copy, setCopy] = useState<CopyState>("idle");
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const fallbackRef = useRef<HTMLTextAreaElement | null>(null);
  const titleId = useId();

  /** Close and hand focus back to the trigger (keyboard dismissal). */
  function closeAndRefocus() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  /** Copy the AI prompt; a rejected/absent clipboard falls back to the textarea. */
  async function copyPrompt() {
    setCopy((await copyToClipboard(AI_IMPORT_PROMPT)) ? "copied" : "failed");
  }

  // Move focus into the popover when it opens so Escape and Tab act on it, and
  // reopen on a clean slate rather than a stale confirmation.
  useEffect(() => {
    if (open) popoverRef.current?.focus();
    else setCopy("idle");
  }, [open]);

  // "Copied!" is a transient confirmation; "failed" sticks until the popover closes.
  useEffect(() => {
    if (copy !== "copied") return;
    const timer = setTimeout(() => setCopy("idle"), COPIED_RESET_MS);
    return () => clearTimeout(timer);
  }, [copy]);

  // Pre-select the fallback text so copying it is one keystroke, not a drag.
  useEffect(() => {
    if (copy === "failed") fallbackRef.current?.select();
  }, [copy]);

  // Escape anywhere, and any pointer press outside the popover, dismiss it.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") closeAndRefocus();
    }
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      // A press on the trigger is its own toggle — don't double-handle it.
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="btn info-btn"
        aria-label="How folder import works"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ⓘ
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="import-help"
          role="dialog"
          aria-labelledby={titleId}
          tabIndex={-1}
        >
          <div className="import-help-head">
            <strong id={titleId}>How folder import works</strong>
            <button type="button" className="btn small ghost" onClick={closeAndRefocus}>
              Close
            </button>
          </div>

          <p className="import-help-intro">
            Folder import is for posting <strong>a whole batch of videos at once</strong> instead of
            creating them one at a time. You give Broadside a folder; inside it, each video sits in
            its own small folder next to a short <strong>details file</strong> that says how to
            title and caption it.
          </p>

          <div className="import-help-ai">
            <p>
              <strong>The easy way — no tech knowledge needed.</strong> You don't have to build any
              of this by hand. Copy the prompt below, paste it into ChatGPT or Claude, describe your
              videos in plain English, and it will create the folders and details files for you.
              Then come back here and pick the folder.
            </p>
            <button type="button" className="btn small" onClick={copyPrompt}>
              {copy === "copied" ? "Copied!" : "Copy the AI prompt"}
            </button>
            <p className="muted small import-help-status" role="status" aria-live="polite">
              {copy === "copied" && "Prompt copied to the clipboard."}
              {copy === "failed" && "Couldn't reach the clipboard — copy the text below instead."}
            </p>
            {copy === "failed" && (
              <textarea
                ref={fallbackRef}
                className="import-help-fallback"
                readOnly
                rows={6}
                aria-label="Prompt text to copy manually"
                value={AI_IMPORT_PROMPT}
              />
            )}
          </div>

          <p className="muted small">
            Prefer to set it up yourself? Here's exactly how it's laid out.
          </p>

          <p>
            Pick the folder that <strong>directly holds one small folder per post</strong>. Each
            post's folder contains the video plus its details file, named <code>post.yaml</code>:
          </p>
          <pre>{`batch_1/            ← pick this folder
  post_01/
    clip.mp4
    post.yaml
  post_02/
    clip.mp4
    post.yaml`}</pre>

          <p className="import-help-warn">
            ⚠ One thing to get right: pick the folder that holds the post folders{" "}
            <em>directly</em>. If yours are nested one level deeper —{" "}
            <code>QUEUE/batch_1/post_01/…</code> — choose <strong>batch_1</strong>, not{" "}
            <code>QUEUE</code>. Picking the outer folder makes Broadside treat each whole batch as a
            single post.
          </p>

          <p>
            The <code>post.yaml</code> is just a small text file listing that post's details — its
            title, caption, and so on. It's written in a simple text format called{" "}
            <strong>YAML</strong>, but you don't need to learn it — the AI prompt above writes these
            for you. A basic one looks like this:
          </p>
          <pre>{`video: clip.mp4
schedule: 2026-07-21T18:00   # optional

platforms:
  youtube:
    title: "Video title"
    description: "Longer description"
  instagram:
    caption: "Caption #tags"
  tiktok:
    caption: "Caption #tags"
  facebook:
    caption: "Caption"
  x:
    text: "Post text"`}</pre>

          <p className="muted small">
            The <code>schedule:</code> line (a date and time in your local timezone, like
            2026-07-21 at 18:00) is optional. Posts that include one publish at that exact time;
            posts without one get spread out automatically at whatever pace you choose after
            importing.
          </p>
        </div>
      )}
    </>
  );
}
